import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Sprout, Sparkles, Cloud, BookOpen, Mail, Lock, ArrowRight, AlertCircle, ShoppingBag, LayoutGrid, Leaf, Compass, Crown, Info } from 'lucide-react';
import { useI18n } from '../i18n';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import InstallPWAButton from '../components/InstallPWAButton';
import AppContent from '../components/AppContent';
import OperatorNotice from '../components/OperatorNotice';
import { nativeGoogleAvailable, signInWithGoogleNative } from '../lib/nativeGoogleAuth';
import { gisAvailable, initGoogleButton } from '../lib/webGoogleAuth';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
    const { t, locale } = useI18n();
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [showEmail, setShowEmail] = useState(false);
    const [email, setEmail] = useState(() => {
        try { return localStorage.getItem('gd_remember_email') || ''; } catch { return ''; }
    });
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const googleBtnRef = useRef(null);

    // Capture ?ref= from URL into localStorage for later use during register/guest
    useEffect(() => {
        const code = (params.get('ref') || '').trim().toUpperCase();
        if (code) {
            try { localStorage.setItem('growdaily_ref', code); } catch { /* ignore */ }
        }
    }, [params]);

    // Prefill of the saved email is handled via lazy initial state above (email only —
    // never the password). The main login screen keeps all options visible.

    const persistEmail = () => {
        try {
            if (remember && email.trim()) localStorage.setItem('gd_remember_email', email.trim().toLowerCase());
            else localStorage.removeItem('gd_remember_email');
        } catch { /* ignore */ }
    };

    // Shared: exchange a Google idToken (from native plugin OR web GIS) for a session.
    const exchangeGoogleToken = async (idToken) => {
        const ref = (localStorage.getItem('growdaily_ref') || '').trim().toUpperCase();
        const r = await api.post('/auth/google-native', { id_token: idToken, locale, ref: ref || undefined });
        if (r.data?.session_token) localStorage.setItem('session_token', r.data.session_token);
        try { localStorage.removeItem('growdaily_ref'); } catch { /* ignore */ }
        setUser(r.data.user);
        window.location.replace('/dashboard');
    };

    // Native app button → native Google account picker (no browser).
    const loginGoogleNative = async () => {
        setBusy(true);
        try {
            const idToken = await signInWithGoogleNative();
            await exchangeGoogleToken(idToken);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : t('login.google_fail'));
            setBusy(false);
        }
    };

    // Web GIS credential callback (your own "GrowDaily" consent — no Emergent).
    const handleGoogleCredential = async (credential) => {
        setBusy(true);
        try {
            await exchangeGoogleToken(credential);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : t('login.google_fail'));
            setBusy(false);
        }
    };

    // Render the official Google button on web (not in the native app, which uses the plugin).
    useEffect(() => {
        if (showEmail || nativeGoogleAvailable() || !gisAvailable() || !googleBtnRef.current) return;
        initGoogleButton(googleBtnRef.current, handleGoogleCredential).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showEmail]);

    const continueAsGuest = async () => {
        setBusy(true);
        try {
            const ref = (localStorage.getItem('growdaily_ref') || '').trim().toUpperCase();
            // A resumed Android WebView can briefly lose its network bridge.
            // Guest creation is server-authoritative, so retry only a failed
            // network/5xx attempt and never retry an explicit 4xx refusal.
            try { localStorage.removeItem('session_token'); } catch { /* ignore */ }
            let r;
            for (let attempt = 0; attempt < 2; attempt += 1) {
                try {
                    r = await api.post('/auth/guest', ref ? { ref } : {});
                    break;
                } catch (err) {
                    const status = err?.response?.status;
                    if (attempt || (status && status < 500)) throw err;
                    await new Promise((resolve) => setTimeout(resolve, 700));
                }
            }
            if (!r?.data?.session_token || !r.data?.user?.user_id) throw new Error('Guest session response was incomplete');
            if (r.data?.session_token) localStorage.setItem('session_token', r.data.session_token);
            try { localStorage.removeItem('growdaily_ref'); } catch { /* ignore */ }
            setUser(r.data.user);
            window.location.replace('/dashboard');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : t('login.guest_fail'));
        } finally { setBusy(false); }
    };

    const sendMagicLink = async () => {
        if (!email.trim() || !email.includes('@')) {
            setError(t('login.enter_email'));
            return;
        }
        setBusy(true);
        try {
            await api.post('/auth/magic-link/send', { email: email.trim().toLowerCase(), origin_url: window.location.origin, locale });
            persistEmail();
            toast.success(t('login.magic_sent'));
        } catch {
            toast.error(t('login.magic_fail'));
        } finally { setBusy(false); }
    };

    const submitEmail = async (e) => {
        e.preventDefault();        setError('');
        setBusy(true);
        try {
            const r = await api.post('/auth/login', { email, password });
            if (r.data?.session_token) localStorage.setItem('session_token', r.data.session_token);
            persistEmail();
            setUser(r.data.user);
            window.location.replace('/dashboard');
        } catch (err) {
            const d = err?.response?.data?.detail;
            const msg = typeof d === 'string' ? d : t('login.wrong');
            setError(msg);
            toast.error(msg);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-brand-bg botanical-noise relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-brand-sage/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-40 -left-32 w-[460px] h-[460px] rounded-full bg-brand-ochre/15 blur-3xl" />

            <div className="relative max-w-2xl mx-auto px-6 py-12 sm:py-20">
                <div className="flex justify-end mb-10">
                    <LanguageSwitcher variant="inline" />
                </div>

                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-brand-green flex items-center justify-center overflow-hidden shadow-sm">
                        <img src="/growdaily-mark.png" alt="GrowDaily" className="w-9 h-9 object-contain" />
                    </div>
                    <div className="font-serif text-xl text-brand-ink">GrowDaily</div>
                </div>

                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-brand-ink mt-8 text-center">
                    {t('login.title')}<span className="text-brand-terracotta">.</span>
                </h1>
                <p className="mt-5 text-base sm:text-lg text-brand-ink2 leading-relaxed max-w-xl mx-auto text-center">
                    {t('login.subtitle')}
                </p>

                {!showEmail ? (
                    <div className="mt-10 space-y-3 w-full max-w-[320px] mx-auto">
                        {nativeGoogleAvailable() ? (
                            <button
                                data-testid="btn-login-google"
                                onClick={loginGoogleNative}
                                className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white px-6 py-3 rounded-full font-medium transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#fff" d="M17.64 9.2c0-.63-.06-1.25-.18-1.84H9v3.48h4.84a4.13 4.13 0 0 1-1.79 2.7v2.24h2.9c1.69-1.55 2.67-3.84 2.67-6.58z"/><path fill="#fff" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.24c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.58-5.05-3.71h-3v2.32A9 9 0 0 0 9 18z" opacity=".75"/><path fill="#fff" d="M3.95 10.73a5.4 5.4 0 0 1 0-3.47V4.95h-3a9 9 0 0 0 0 8.1l3-2.32z" opacity=".5"/><path fill="#fff" d="M9 3.58c1.32 0 2.5.45 3.44 1.35L15 2.34A9 9 0 0 0 .96 4.96l3 2.32C4.66 5.16 6.65 3.58 9 3.58z" opacity=".25"/></svg>
                                {t('login.cta')}
                            </button>
                        ) : (
                            <div ref={googleBtnRef} data-testid="google-signin-button" className="flex justify-center min-h-[40px]" />
                        )}
                        <button
                            data-testid="btn-show-email-login"
                            onClick={() => setShowEmail(true)}
                            className="w-full inline-flex items-center justify-center gap-2 bg-brand-card hover:bg-white border border-brand-border text-brand-ink px-6 py-3 rounded-full font-medium transition-all"
                        >
                            <Mail size={16} strokeWidth={1.8} /> {t('login.email')}
                        </button>
                        <button
                            data-testid="btn-login-guest"
                            onClick={continueAsGuest}
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 text-brand-ink2 hover:text-brand-green underline-offset-4 hover:underline px-6 py-2 text-sm font-medium"
                        >
                            {t('login.guest')} <ArrowRight size={14} strokeWidth={1.8} />
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submitEmail} className="mt-10 max-w-sm space-y-3 mx-auto text-left">
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Email</span>
                            <div className="mt-1 relative">
                                <Mail size={15} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-ink3" />
                                <input data-testid="input-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-brand-card border border-brand-border rounded-full pl-11 pr-4 py-3 text-brand-ink" placeholder="dig@email.dk" />
                            </div>
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('register.password')}</span>
                            <div className="mt-1 relative">
                                <Lock size={15} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-ink3" />
                                <input data-testid="input-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-brand-card border border-brand-border rounded-full pl-11 pr-4 py-3 text-brand-ink" placeholder="••••••••" />
                            </div>
                        </label>
                        {error && (
                            <div data-testid="login-error" className="flex items-start gap-2 text-sm text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/20 rounded-2xl p-3">
                                <AlertCircle size={16} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}
                        <label className="flex items-center gap-2.5 cursor-pointer select-none py-1">
                            <input
                                data-testid="checkbox-remember-me"
                                type="checkbox"
                                checked={remember}
                                onChange={(e) => setRemember(e.target.checked)}
                                className="w-4 h-4 rounded border-brand-border text-brand-green accent-brand-green focus:ring-brand-green/40"
                            />
                            <span className="text-sm text-brand-ink2">{t('login.remember')}</span>
                        </label>
                        <button data-testid="btn-submit-login" disabled={busy} type="submit" className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50">
                            {busy ? t('login.signingin') : t('login.submit')}
                        </button>
                        <button
                            type="button"
                            onClick={sendMagicLink}
                            disabled={busy}
                            data-testid="btn-magic-link"
                            className="w-full inline-flex items-center justify-center gap-2 text-sm text-brand-ink3 hover:text-brand-green border border-dashed border-brand-border rounded-full py-2.5 disabled:opacity-50"
                        >
                            <Sparkles size={14} /> {t('login.magic')}
                        </button>
                        <div className="flex items-center justify-between text-sm pt-1">
                            <button type="button" onClick={() => setShowEmail(false)} className="text-brand-ink3 hover:text-brand-ink">{t('login.back')}</button>
                            <Link to="/register" className="text-brand-green font-medium hover:text-brand-greenDark">{t('login.create')}</Link>
                        </div>
                    </form>
                )}

                <div className="mt-6 max-w-sm mx-auto flex items-start gap-2.5 text-xs text-brand-ink3 bg-brand-card/60 border border-brand-border rounded-2xl px-4 py-3" data-testid="login-cache-notice">
                    <Info size={15} strokeWidth={1.8} className="flex-shrink-0 mt-0.5 text-brand-ink3" />
                    <span>{t('login.cant_login_hint')}</span>
                </div>

                <div className="mt-6 flex justify-center" data-testid="login-install-btn">
                    <InstallPWAButton variant="inline" />
                </div>

                <div className="mt-16">
                    <div className="flex items-center justify-center gap-2 mb-5">
                        <span className="h-px w-8 bg-brand-border" />
                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-500">Alt-i-én haveapp</span>
                        <span className="h-px w-8 bg-brand-border" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        <Feature icon={Cloud} color="#0EA5E9" title={t('login.feat_weather')} desc={t('login.feat_weather_sub')} />
                        <Feature icon={Sprout} color="#16A34A" title={t('login.feat_garden')} desc={t('login.feat_garden_sub')} />
                        <Feature icon={Sparkles} color="#A855F7" title={t('login.feat_ai')} desc={t('login.feat_ai_sub')} />
                        <Feature icon={BookOpen} color="#F97316" title={t('login.feat_guides')} desc={t('login.feat_guides_sub')} />
                        <Feature icon={ShoppingBag} color="#EC4899" title={t('login.feat_market')} desc={t('login.feat_market_sub')} />
                        <Feature icon={LayoutGrid} color="#14B8A6" title={t('login.feat_beds')} desc={t('login.feat_beds_sub')} />
                        <Feature icon={Leaf} color="#65A30D" title={t('login.feat_forage')} desc={t('login.feat_forage_sub')} />
                        <Feature icon={Compass} color="#D97706" title={t('login.feat_wild')} desc={t('login.feat_wild_sub')} />
                    </div>
                </div>

                {/* Premium teaser → prices & signup */}
                <button
                    type="button"
                    onClick={() => navigate('/premium')}
                    className="mt-6 w-full text-left relative overflow-hidden rounded-3xl border border-amber-300/40 bg-brand-card/80 backdrop-blur p-5 flex items-center gap-4 transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
                    data-testid="login-premium-teaser"
                >
                    <div className="pointer-events-none absolute -top-8 -right-6 w-32 h-32 rounded-full bg-amber-300/20 blur-2xl" />
                    <div className="w-12 h-12 rounded-2xl bg-amber-400 text-brand-ink flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Crown size={22} strokeWidth={1.9} />
                    </div>
                    <div className="relative min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-serif text-lg text-brand-ink">{t('login.premium_tag')}</span>
                            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-300/40 rounded-full px-2 py-0.5">{t('login.premium_badge')}</span>
                        </div>
                        <p className="text-sm text-brand-ink2 mt-0.5">{t('login.premium_line')}</p>
                    </div>
                </button>

                <AppContent placement="login" className="mt-6" />

                <footer className="mt-12 text-center" data-testid="login-credit">
                    <OperatorNotice />
                    <p className="text-[11px] text-brand-ink3">
                        {t('profile.app_by')}{' '}
                        <a href="https://uniqapps.space" target="_blank" rel="noreferrer" className="text-brand-green hover:underline font-medium">
                            Uniqapps.space
                        </a>
                    </p>
                </footer>
            </div>
        </div>
    );
}

function Feature({ icon: Icon, title, desc, color }) {
    return (
        <div className="bg-brand-card/70 backdrop-blur border border-brand-border rounded-2xl p-4 transition-all hover:-translate-y-0.5 hover:border-brand-green/40">
            <Icon size={18} strokeWidth={1.7} className="mb-2" style={{ color: color || '#16A34A' }} />
            <div className="font-serif text-base text-amber-600">{title}</div>
            <div className="text-xs text-brand-ink3">{desc}</div>
        </div>
    );
}
